import React, { useCallback, useMemo } from "react";
import { Attachment, Job } from "../../../models/Job";
import { NoteData, NoteType } from "../../../models/NoteData";
import { jobService } from "../../../services/JobService";
import { Button, Container, Stack, TextField, Typography } from "@mui/material";
import { FileList } from "../../../components/attachments/FileList";
import { isEmpty, sortBy } from "lodash";
import { DateTime } from "luxon";
import { InstallerJobQuestionnaire, InstallerQuestions } from "../../../models/InstallerJobQuestionnaire";
import { InstallerQuestion } from "../../installer/job/components/InstallerQuestion";

export interface IJobMediaReportProps {
    jobId: string;
    failedToLoad: () => void;
}

export const JobMediaReport = React.memo(({ jobId, failedToLoad }: IJobMediaReportProps) => {
    const [job, setJob] = React.useState<Job | null>(null);
    const [questions, setQuestions] = React.useState<InstallerJobQuestionnaire[]>(InstallerQuestions);
    const [onArriveAttachments, setOnArriveAttachments] = React.useState<Attachment[]>([]);
    const [onFinishedAttachments, setOnFinishedAttachments] = React.useState<Attachment[]>([]);
    const [notes, setNotes] = React.useState<NoteData[]>([]);
    const [copyBtnText, setCopyBtnText] = React.useState("Copy");

    const loadJobData = useCallback(async () => {
        try {
            const loadedJob = await jobService.fetchJobByIdAsync(jobId);
            if (!loadedJob) {
                return failedToLoad();
            }
            setJob(loadedJob);
            const attchements = await jobService.getJobAttachments(jobId);
            if (attchements.length) {
                setOnArriveAttachments(attchements.filter((attachment) => attachment.job_stage === "in-progress"));
                setOnFinishedAttachments(attchements.filter((attachment) => attachment.job_stage === "completed"));
            }
            const jobNotes = await jobService.getJobNotesAsync(jobId);
            if (jobNotes.length) {
                setNotes(jobNotes);
            }
        } catch (error) {
            failedToLoad();
        }
    }, [failedToLoad, jobId]);
    const loadQuestions = useCallback(async () => {
        jobService.getQuestionnaire(jobId).then((questions) => {
            if (isEmpty(questions)) {
                return;
            }
            const sortedQuestions = questions.sort((a, b) => a.order - b.order);
            // merge with default questions
            const mergedQuestions = InstallerQuestions.map((defaultQuestion) => {
                const existingQuestion = sortedQuestions.find((q) => q.question === defaultQuestion.question);
                return existingQuestion ? { ...defaultQuestion, ...existingQuestion } : defaultQuestion;
            });
            setQuestions(mergedQuestions);
        });
    }, [jobId]);

    React.useEffect(() => {
        loadJobData();
        loadQuestions();
    }, [loadJobData, loadQuestions]);

    const beforeWorkStartLabel = useMemo(() => {
        const uploadMadeTime = sortBy(onArriveAttachments, ["uploaded_datetime"])[0]?.uploaded_datetime;
        const uploadMade = uploadMadeTime
            ? `upload made at ${DateTime.fromISO(uploadMadeTime, {
                  zone: "utc",
                  setZone: true,
              }).toLocaleString(DateTime.DATETIME_SHORT)}`
            : "No uploaded attachments";
        return (
            <Stack direction="row" mb={8} spacing={1} justifyContent={"space-between"}>
                <Typography fontWeight="bold">Before Work Started</Typography>
                <Typography>{uploadMade}</Typography>
            </Stack>
        );
    }, [onArriveAttachments]);
    const afterWorkFinishedLabel = useMemo(() => {
        const uploadMadeTime = sortBy(onFinishedAttachments, ["uploaded_datetime"])[0]?.uploaded_datetime;
        const uploadMade = uploadMadeTime
            ? `upload made at ${DateTime.fromISO(uploadMadeTime, {
                  zone: "utc",
                  setZone: true,
              }).toLocaleString(DateTime.DATETIME_SHORT)}`
            : "No uploaded attachments";
        return (
            <Stack direction="row" mb={8} spacing={1} justifyContent={"space-between"}>
                <Typography fontWeight="bold">After Worker Finished</Typography>
                <Typography>{uploadMade}</Typography>
            </Stack>
        );
    }, [onFinishedAttachments]);

    const renderNote = useCallback((note?: NoteData) => {
        if (!note) {
            return null;
        }
        return (
            <TextField
                style={{
                    marginBottom: 8,
                }}
                className="review-media-note"
                variant="outlined"
                label="Notes from Worker"
                value={note.note_text}
                fullWidth
                multiline
                disabled
            />
        );
    }, []);

    const copyBtnClick = useCallback(() => {
        // get pathname
        const pathname = window.location.pathname;
        // form url
        const url = `${window.location.origin}${pathname}?jobId=${jobId}`;
        // copy to clipboard
        navigator.clipboard.writeText(url).then(() => {
            setCopyBtnText("Copied");
            setTimeout(() => {
                setCopyBtnText("Copy");
            }, 2000);
        });
    }, [jobId]);

    const onDownloadReport = useCallback(() => {
        jobService.downloadMediaReport(jobId);
    }, [jobId]);

    if (!job) {
        return null;
    }

    return (
        <Container maxWidth={false} sx={{ padding: 0 }} disableGutters>
            <Stack direction={"row"} spacing={2} justifyContent={"space-between"}>
                <Typography variant="h4" fontWeight={"bolder"} sx={{ marginBottom: 2 }}>
                    Report
                </Typography>
                <Stack direction={"row"}>
                    <Button
                        style={{
                            marginRight: 10,
                        }}
                        className="basic-button"
                        onClick={copyBtnClick}>
                        {copyBtnText}
                    </Button>
                    <Button className="basic-button" onClick={onDownloadReport}>
                        Download PDF
                    </Button>
                </Stack>
            </Stack>
            <FileList customLabel={beforeWorkStartLabel} attachments={onArriveAttachments} />

            {questions
                .filter((q) => q.section === "before_installation")
                .map((question, index) => {
                    return (
                        <InstallerQuestion
                            questionNumber={index + 1}
                            key={question.id ?? index}
                            question={question}
                            readOnly={true}
                            onChange={() => {
                                // do nothing, read-only
                            }}
                        />
                    );
                })}
            {renderNote(notes.find((note) => note.note_type === NoteType.in_progress_attachment))}
            <FileList customLabel={afterWorkFinishedLabel} attachments={onFinishedAttachments} direction="row" />
            {questions
                .filter((q) => q.section === "after_installation")
                .map((question, index) => {
                    return (
                        <InstallerQuestion
                            questionNumber={index + 2}
                            key={question.id ?? index}
                            question={question}
                            readOnly={true}
                            onChange={() => {
                                // do nothing, read-only
                            }}
                        />
                    );
                })}
            {renderNote(notes.find((note) => note.note_type === NoteType.completed_attachment))}
        </Container>
    );
});

JobMediaReport.displayName = "JobMediaReport";
